/**
 * 🎨 [Phase 103 — 2026-07-09] Wrapper Dialog dédié à la fiche employé.
 *
 * Rôle
 * ────
 * Compose un modal shadcn/Radix avec :
 *  - **Overlay clair** (`bg-slate-900/15` au lieu du `bg-black/80` global)
 *    → la page derrière reste lisible, cohérent avec le look général.
 *  - **Bouton X aligné et visible** (`right-5 top-5`, fond blanc, ombre, `h-8 w-8`).
 *  - Aucun impact sur les autres modals de l'app (les Dialog de shadcn continuent
 *    d'utiliser leur `DialogOverlay` d'origine).
 *
 * Usage
 * ─────
 * ```jsx
 * <EmployeeDetailDialog open={showDetail} onOpenChange={setShowDetail}>
 *   <div>Contenu de la fiche…</div>
 * </EmployeeDetailDialog>
 * ```
 */
import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export const EmployeeDetailDialog = ({
  open,
  onOpenChange,
  children,
  className,
  contentTestId = "employee-detail-dialog",
}) => {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* 🎨 [Phase 103] Overlay clair (au lieu du bg-black/80 global) */}
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-slate-900/15 backdrop-blur-[2px]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
          data-testid="employee-detail-overlay"
        />
        <DialogPrimitive.Content
          data-testid={contentTestId}
          className={cn(
            "fixed left-[50%] top-[50%] z-50 grid w-full max-w-5xl translate-x-[-50%] translate-y-[-50%]",
            "gap-4 border border-slate-200 bg-white p-6 shadow-xl duration-200",
            "max-h-[90vh] overflow-y-auto sm:rounded-xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            className
          )}
        >
          {children}
          {/* 🎨 [Phase 103] X redesigné : plus grand, visible, aligné, sur fond blanc pill. */}
          <DialogPrimitive.Close
            data-testid="employee-detail-close-btn"
            aria-label="Fermer la fiche employé"
            className={cn(
              "absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full",
              "border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors",
              "hover:bg-slate-100 hover:text-slate-900",
              "focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1",
              "disabled:pointer-events-none"
            )}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Fermer</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default EmployeeDetailDialog;
